import { formatFullDate, formatMonthDay } from '@trips/shared/format';
import type { Benefit } from '@trips/shared/schema';
import { programInfo } from '@trips/shared/valuations';
import { patch } from '../../data/mutate';
import { selectPrefs, selectWallet } from '../../data/selectors';
import { useStore } from '../../data/store';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { TopBar } from '../../ui/TopBar';
import s from './Wallet.module.css';
import { usd } from './WalletScreen';

const PERIOD: Record<Benefit['period'], string> = {
  monthly: 'Each month',
  quarterly: 'Each quarter',
  semiannual: 'Twice a year',
  annual: 'Each calendar year',
  'cardmember-year': 'Each card year',
  once: 'Once',
  ongoing: 'Always on',
};

export function CardScreen({ id }: { id: string }) {
  const card = useStore((st) => st.tables.cards.get(id));
  const wallet = useStore(selectWallet);
  const prefs = useStore(selectPrefs);
  if (!card || card.deleted)
    return (
      <div>
        <TopBar backTo="/wallet" />
        <EmptyState icon="credit-card" title="This card isn't here" />
      </div>
    );

  const earns = programInfo(card.earns);
  const program = wallet.programs.find((p) => p.programId === card.earns);
  const credits = card.benefits.filter((b) => b.value != null);
  const perks = card.benefits.filter((b) => b.value == null);
  const creditTotal = credits.reduce(
    (n, b) =>
      n +
      (b.period === 'monthly'
        ? 12
        : b.period === 'quarterly'
          ? 4
          : b.period === 'semiannual'
            ? 2
            : 1) *
        (b.value ?? 0),
    0,
  );

  const markUsed = (b: Benefit) =>
    void patch('cards', card.id, {
      benefits: card.benefits.map((x) => (x.id === b.id ? { ...x, used: x.value } : x)),
    });

  return (
    <div>
      <TopBar title={card.name} backTo="/wallet" />
      <div className={s.detail}>
        <div className={s.hero}>
          <h1 className="t-title1">{card.name}</h1>
          <p className={s.meta}>
            {card.issuer}
            {card.last4 ? `, ending ${card.last4}` : ''}
          </p>
        </div>

        <div className={s.facts}>
          <div className={s.fact}>
            <span className={s.factLabel}>Annual fee</span>
            <span className={`${s.factValue} num`}>{usd(card.annualFee)}</span>
          </div>
          <div className={s.fact}>
            <span className={s.factLabel}>Renews</span>
            <span className={`${s.factValue} num`}>
              {card.feeDueOn ? formatMonthDay(card.feeDueOn, prefs) : '–'}
            </span>
          </div>
          <div className={s.fact}>
            <span className={s.factLabel}>Credits a year</span>
            <span className={`${s.factValue} num`}>{creditTotal ? usd(creditTotal) : '–'}</span>
          </div>
        </div>

        {credits.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>Credits</h2>
            <div className={s.rows}>
              {credits.map((b) => {
                const left = Math.max(0, (b.value ?? 0) - (b.used ?? 0));
                return (
                  <div key={b.id} className={s.row}>
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>{b.title}</span>
                      <span className={s.rowDetail}>
                        {PERIOD[b.period]}
                        {b.resetsOn ? `, resets ${formatMonthDay(b.resetsOn, prefs)}` : ''}
                      </span>
                      <span className={s.bar}>
                        <span
                          style={{
                            width: `${Math.min(100, ((b.used ?? 0) / (b.value || 1)) * 100)}%`,
                          }}
                        />
                      </span>
                    </span>
                    <span className={s.rowRight}>
                      <span className={`${s.rowValue} num`}>
                        {left ? `${usd(left)} left` : 'Used'}
                      </span>
                      {left > 0 && (
                        <Button variant="ghost" onClick={() => markUsed(b)}>
                          Mark used
                        </Button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {perks.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>Benefits</h2>
            <div className={s.rows}>
              {perks.map((b) => (
                <div key={b.id} className={s.row}>
                  <span className={s.rowText}>
                    <span className={s.rowTitle}>{b.title}</span>
                    {b.note && <span className={s.rowDetail}>{b.note}</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {earns && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>Earns</h2>
            <div className={s.rows}>
              <button
                type="button"
                className={s.row}
                onClick={() => program && navigate(`/wallet/points/${program.id}`)}
              >
                <span className={s.rowText}>
                  <span className={s.rowTitle}>{earns.name}</span>
                  <span className={s.rowDetail}>Worth about {earns.typicalCpp}¢ a point</span>
                </span>
              </button>
            </div>
          </section>
        )}

        {card.notes && <p className={s.notes}>{card.notes}</p>}
        {card.openedOn && <p className={s.meta}>Opened {formatFullDate(card.openedOn, prefs)}</p>}
        <div className={s.actions}>
          <Button
            icon="pencil-simple"
            onClick={() => navigate(`/wallet/edit/card/${card.id}`, { overlay: true })}
          >
            Edit card
          </Button>
        </div>
      </div>
    </div>
  );
}
